import React, { useEffect, useCallback, useState, useContext, useRef, } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import ReactModal from 'react-modal';
import { SisuContext } from '../../Context';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { ThemeOverride, ThemeOptions, } from '@wix/da-react-context/pkg/ThemeContext';
import CloseButtonThin from '../../Primitives/CloseButton';
import CloseIconBold from '@wix/da-ds/pkg/Icons/CloseBold';
import IconButton from '../../Button/IconButton';
import ErrorBoundary from '../../ErrorBoundary';
import { useBodyScrollLock, } from '@wix/da-ds/pkg/hooks/useBodyScrollLock';
import { PopupContext } from '@wix/da-ds/pkg/popups/PopupContext';
import { useVisualViewport, } from '@wix/da-ds/pkg/hooks/useVisualViewport';
import ddt from '@wix/da-ddt';
import LoadingIndicator from '../../LoadingIndicator';
import s from './Modal.scss';
var Modal = function (_a) {
    var _b = _a.isOpen, isOpen = _b === void 0 ? false : _b, _c = _a.isLoading, isLoading = _c === void 0 ? false : _c, onRequestClose = _a.onRequestClose, width = _a.width, height = _a.height, className = _a.className, closeBtnClassName = _a.closeBtnClassName, closeBtnDisableDefaultColors = _a.closeBtnDisableDefaultColors, closeBtnButtonClassName = _a.closeBtnButtonClassName, closeBtnSize = _a.closeBtnSize, _d = _a.contentClassName, contentClassName = _d === void 0 ? className : _d, contentLabel = _a.contentLabel, disableAriaHide = _a.disableAriaHide, _e = _a.withCloseButton, withCloseButton = _e === void 0 ? true : _e, _f = _a.shouldCloseOnOverlayClick, shouldCloseOnOverlayClick = _f === void 0 ? true : _f, shouldReturnFocusAfterClose = _a.shouldReturnFocusAfterClose, overlayStyle = _a.overlayStyle, overlayClassName = _a.overlayClassName, portalClassName = _a.portalClassName, forceTheme = _a.forceTheme, allowThemeSkins = _a.allowThemeSkins, bodyScrollLockOptions = _a.bodyScrollLockOptions, children = _a.children, useMobileFixedHeight = _a.useMobileFixedHeight;
    var t = useTranslation().t;
    var isMobile = useContext(MobileContext);
    var style = {
        content: {
            width: width,
            height: height,
        },
        overlay: overlayStyle,
    };
    // Mobile fixed to viewport fixes
    var _g = useState(), visualViewport = _g[0], setVisualViewport = _g[1];
    var viewportHandler = useCallback(function (v) {
        setVisualViewport(v);
    }, []);
    useVisualViewport(viewportHandler, isMobile === true && useMobileFixedHeight === true);
    var contentStyle = {};
    // Apply fixed height for mobile if requested, helper for fixing Safari being lame
    if (isMobile && visualViewport && !height) {
        style.content.height = visualViewport.height;
        style.content.transform = "translate(".concat(visualViewport.offsetLeft, "px,").concat(visualViewport.offsetTop, "px)");
        style.content.scale = visualViewport.scale;
        // Calculate positions from top left
        style.content.top = 0;
        style.content.left = 0;
        style.content.position = 'fixed';
        // Set the max height of the content, fixes content expanding beyond viewport
        contentStyle.maxHeight = visualViewport.height;
        ddt.log('popup', 'Adjusting to viewport offset', visualViewport);
    }
    // Mobile body scroll locking
    var contentRef = useRef(null);
    var _h = useBodyScrollLock(bodyScrollLockOptions), onLock = _h.onLock, onUnlock = _h.onUnlock, setScrollableRef = _h.setScrollableRef, scrollableElementRef = _h.scrollableElementRef;
    useEffect(function () {
        var focusedEl = document.activeElement;
        var contentEl = contentRef.current;
        var isFocused = function (el) { return el === focusedEl || el.contains(focusedEl); };
        var needsFocus = contentEl && (!focusedEl || !isFocused(contentEl));
        var focusTimeout;
        if (needsFocus) {
            // useEffect is supposed to be invoked when everything mounted, but without this timeout something else steals the focus
            focusTimeout = setTimeout(function () { return contentEl.focus(); }, 100);
        }
        return function () { return focusTimeout && clearTimeout(focusTimeout); };
    }, []);
    var setContentRef = function (instance) {
        contentRef.current = instance;
        // It might happen that the Popup renders a component that calls setScrollableRef on its own, such as ScrollableContent.
        // In this case we avoid updating the ref because that would cause an incorrect re-lock to the modal content element.
        if (!scrollableElementRef.current) {
            setScrollableRef(instance);
        }
    };
    var onClose = useCallback(function () {
        onUnlock();
        onRequestClose && onRequestClose();
    }, [onRequestClose, onUnlock]);
    return (React.createElement(ReactModal, { isOpen: isOpen, onAfterOpen: onLock, onRequestClose: onClose, shouldCloseOnOverlayClick: shouldCloseOnOverlayClick, shouldReturnFocusAfterClose: shouldReturnFocusAfterClose, className: classnames(s['modal'], className), overlayClassName: classnames(s['overlay'], overlayClassName), portalClassName: classnames('ReactModalPortal', portalClassName), contentLabel: contentLabel, style: style, ariaHideApp: !disableAriaHide, contentRef: setContentRef },
        React.createElement(ThemeOverride, { className: classnames(!allowThemeSkins && 'no-theme-skin', contentClassName), theme: forceTheme || ThemeOptions.LIGHT, style: contentStyle },
            withCloseButton && (React.createElement(SisuContext.Consumer, null, function (isSisu) {
                return isSisu ? (React.createElement(CloseButtonThin, { className: classnames(s['sisu-close-button'], closeBtnClassName), onClick: onClose })) : (React.createElement(IconButton, { className: classnames(s['close-button'], closeBtnButtonClassName), onClick: onClose, "aria-label": t('common.close'), noHoverEffect: true },
                    React.createElement(CloseIconBold, { className: closeBtnClassName, size: closeBtnSize, disableDefaultColors: closeBtnDisableDefaultColors })));
            })),
            React.createElement(ErrorBoundary, { debugComponent: "Modal" },
                React.createElement(PopupContext.Provider, { value: {
                        isInPopup: true,
                        setScrollableContentRef: setScrollableRef,
                    } }, isLoading ? React.createElement(LoadingIndicator, null) : children)))));
};
Modal.displayName = 'Modal';
export default Modal;
//# sourceMappingURL=Modal.js.map